import { GemMark } from "@/components/c360/GemMark";
import { MARKET_LABEL as MKT_LABEL } from "@/lib/markets";
import { Link } from "react-router-dom";
import { ArrowUpDown, ExternalLink, Send } from "lucide-react";
import { liveClick } from "@/lib/liveDrawer";
import { useDemoBlur } from "@/lib/demoBlur";
import { bl } from "@/lib/c360Mine";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { UnaffiliatedBadge } from "@/components/UnaffiliatedBadge";
import { StarButton } from "@/components/Stars";

// v65 — Live Streaming Intel table pieces. Wording is fixed by the brief.
export const TIER_COLORS = { 1: "#39FF14", 2: "#00F0FF", 3: "#FBBF24", 4: "#94A3B8" };
export const MARKET_LABEL = { ...MKT_LABEL, forex: "Forex", unaffiliated: "Unaffiliated" };
export const OFTEN = { frequent: "Frequent", regular: "Regular", rare: "Rare" };
export const CHAT = {
  active: { label: "Active", dot: "#39FF14", hint: "Active: a busy live chat." },
  quiet: { label: "Quiet", dot: "#FBBF24", hint: "Quiet: some live chat, not much." },
  very_quiet: { label: "Very quiet", dot: "#FB923C", hint: "Very quiet: barely any live chat." },
  chat_off: { label: "Chat off", dot: "#71717A", hint: "Chat off: this creator keeps live chat turned off." },
};
export const CHAT_NONE_HINT = "Chat not read yet. Waiting for more live streams to measure.";
export const BOT = {
  high: { label: "High", color: "#F43F5E", hint: "Most of the live viewer count is likely not real. Judge this creator by chat and views, not the live count." },
  medium: { label: "Medium", color: "#FB923C", hint: "Part of the live viewer count is likely not real. Treat the live number with caution." },
  low: { label: "Low", color: "#A1A1AA", hint: "Mostly fine. A small oddity in the live numbers, but the crowd looks mostly real." },
  none: { label: "No sign", color: "#39FF14", hint: "Nothing unusual found. The live crowd looks like a real audience." },
  small: { label: "Small audience", color: "#71717A", hint: "Small live audience. Bot check starts once live viewership grows." },
  not_enough: { label: "Not enough data yet", color: "#71717A", hint: "Needs a few more measured streams. This updates automatically." },
};
export const HEAD_HINT = {
  viewers: "Typical live viewers across measured streams in the last 90 days (all time if none in 90 days), with the typical peak below.",
  often: "Frequent: about 3+ lives a week. Regular: about weekly. Rare: less often.",
  bot: "Signs of bought live viewers in the live numbers. Updated regularly; a creator's status can change over time.",
};
export const SORT_LABEL = { name: "Creator", subs: "Subscribers", viewers: "Live viewers", often: "Frequency", chat: "Chat", last_live: "Last live", bot: "Live bot check", exchanges: "Exchanges", match: "Match" };
export const DEFAULT_SORT = [{ key: "viewers", order: "desc" }];

// Click sorts by one column (again flips it); Shift+click adds a 2nd or 3rd sort.
export function nextSort(sort, k, shift) {
  const found = sort.find((x) => x.key === k);
  if (shift) return found ? sort.map((x) => (x.key === k ? { ...x, order: x.order === "desc" ? "asc" : "desc" } : x)) : [...sort, { key: k, order: "desc" }];
  if (found && sort.length === 1) return [{ key: k, order: found.order === "desc" ? "asc" : "desc" }];
  return [{ key: k, order: k === "name" ? "asc" : "desc" }];
}

export const sortText = (sort, labels = SORT_LABEL) => sort.map((s) => `${labels[s.key]} ${s.order === "desc" ? "↓" : "↑"}`).join(", then ");
const isDefaultSort = (sort) => sort.length === 1 && sort[0].key === "viewers" && sort[0].order === "desc";

export function SortLine({ sort, labels, onReset, prefix }) {
  return (
    <span data-testid={`${prefix}-sort-line`}>
      Sorted by {sortText(sort, labels)}
      {!isDefaultSort(sort) && <button type="button" onClick={onReset} data-testid={`${prefix}-sort-reset`} className="ml-2 underline text-zinc-300 hover:text-white">Reset sort</button>}
    </span>
  );
}

export function short(n) {
  if (n == null) return "—";
  const f = (v, u) => (v >= 100 ? v.toFixed(0) : v >= 10 ? v.toFixed(1) : v.toFixed(2)).replace(/(\.\d*?)0+$/, "$1").replace(/\.$/, "") + u;
  if (n >= 1e6) return f(n / 1e6, "M");
  if (n >= 1e3) return f(n / 1e3, "K");
  return String(n);
}

export function Hint({ text, children, testid }) {
  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild><span data-testid={testid} data-hint={text} className="cursor-help">{children}</span></TooltipTrigger>
      <TooltipContent className="max-w-[280px] p-2.5 text-xs leading-snug text-zinc-300 border hairline" style={{ background: "var(--surface-2)" }} data-testid={testid ? `${testid}-tip` : undefined}>{text}</TooltipContent>
    </Tooltip>
  );
}

export function SortHead({ k, sort, onSort, hint, labels = SORT_LABEL, prefix = "li" }) {
  const idx = sort.findIndex((s) => s.key === k);
  const on = idx >= 0;
  const label = labels[k];
  return (
    <th className="px-3 py-2.5 text-left font-normal whitespace-nowrap">
      <span className="inline-flex items-center gap-1">
        <button type="button" data-testid={`${prefix}-sort-${k}`} onClick={(e) => onSort(k, e.shiftKey)} title="Shift-click for a second sort"
          className={`inline-flex items-center gap-1 mono text-[10px] uppercase tracking-[0.15em] transition-colors ${on ? "text-white" : "text-zinc-500 hover:text-zinc-300"}`}>
          {hint ? <Hint text={hint} testid={`${prefix}-head-hint-${k}`}>{label}</Hint> : label}
          <ArrowUpDown size={11} className={on ? "text-cyan-400" : "text-zinc-600"} />
          {on && <span className="mono text-[9px]" data-testid={`${prefix}-sort-dir-${k}`}>{sort[idx].order === "desc" ? "↓" : "↑"}</span>}
          {on && sort.length > 1 && <span className="mono text-[9px] px-1 rounded" data-testid={`${prefix}-sort-badge-${k}`} style={{ background: "rgba(0,240,255,0.2)", color: "#00F0FF" }}>{idx + 1}</span>}
        </button>
      </span>
    </th>
  );
}

export function ChatCell({ r, speed }) {
  const c = CHAT[r.chat];
  if (!c) return <Hint text={CHAT_NONE_HINT} testid={`li-chat-${r.id}`}><span className="inline-block px-3 py-1 -mx-3 text-zinc-600">—</span></Hint>;
  return (
    <Hint text={c.hint} testid={`li-chat-${r.id}`}>
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-zinc-200">
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.dot }} />
        {c.label}{speed && r.speed != null ? <span className="mono text-zinc-400"> · {r.speed}/min</span> : null}
        {r.n_chat > 0 && <span className="mono text-[10px] text-zinc-500" data-testid={`li-chat-count-${r.id}`}>({r.n_chat})</span>}
      </span>
    </Hint>
  );
}

export function BotCell({ r }) {
  const b = BOT[r.bot] || BOT.not_enough;
  return (
    <Hint text={b.hint} testid={`li-bot-${r.id}`}>
      <span className="mono text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap" data-verdict={r.bot}
        style={{ color: b.color, border: `1px solid ${b.color}55`, background: `${b.color}14` }}>{b.label}</span>
    </Hint>
  );
}

export const lastLiveColor = (days) => (days <= 1 ? "#39FF14" : days <= 7 ? "#FBBF24" : "#71717A");
export const lastLiveText = (days) => (days === 0 ? "Today" : days === 1 ? "1 day ago" : `${days} days ago`);

export function LastLive({ r }) {
  if (r.days == null) return <span className="text-zinc-600">—</span>;
  return <span style={{ color: lastLiveColor(r.days) }} className="whitespace-nowrap">{lastLiveText(r.days)}</span>;
}

const pill = (active) => active
  ? { background: "rgba(57,255,20,0.10)", color: "#39FF14", border: "1px solid rgba(57,255,20,0.55)" }
  : { background: "rgba(244,63,94,0.10)", color: "#F43F5E", border: "1px solid rgba(244,63,94,0.55)" };

// v70 — same days and hover text as Creator Intel's Tenure column.
function ExBadge({ e, id, hl, own }) {
  const has = e.days != null || e.days_since_last != null;
  const days = e.active ? `${e.days}d` : `${e.days_since_last}d off`;
  const key = e.name.toLowerCase();
  const mine = own && key === own;
  const title = has ? `${e.name}${mine ? " (your exchange)" : ""} · tenure ${e.days}d · last video ${e.days_since_last}d ago · ${e.active ? "active" : "offboarded (30d+ silent)"}` : undefined;
  return (
    <span data-testid={`li-ex-${id}-${e.name}`} data-status={e.active ? "active" : "stopped"} data-picked={hl?.has(key) ? "true" : undefined} title={title}
      className={`mono text-[10px] leading-[16px] h-[22px] px-1.5 rounded inline-flex items-center justify-between gap-2 ${hl?.has(key) ? "font-bold" : "font-medium"}`} style={pill(e.active)}>
      <span className="truncate">{mine && <span className="mr-1" data-testid={`li-ex-own-${id}`}>★</span>}{e.name}</span>
      {has && <span className="text-[9px] opacity-80 shrink-0" data-testid={`li-ex-days-${id}-${e.name}`}>{days}</span>}
    </span>
  );
}

export function Exchanges({ r, hl, own }) {
  if (r.segment === "unaffiliated") return <UnaffiliatedBadge compact />;
  if (!r.exchanges.length) return <span className="text-zinc-600">—</span>;
  const sorted = hl ? [...r.exchanges].sort((a, b) => hl.has(b.name.toLowerCase()) - hl.has(a.name.toLowerCase())) : r.exchanges;
  const shown = sorted.slice(0, 4);
  const rest = sorted.slice(4);
  return (
    <div className="flex flex-col gap-1 max-w-[180px]">
      {shown.map((e) => <ExBadge key={`${e.cat}-${e.name}`} e={e} id={r.id} hl={hl} own={own} />)}
      {rest.length > 0 && <Hint text={rest.map((e) => `${e.name}${e.active ? "" : " (stopped)"}`).join(", ")} testid={`li-ex-more-${r.id}`}><span className="mono text-[10px] text-zinc-500 pl-1">+{rest.length} more</span></Hint>}
    </div>
  );
}

export function LiveRow({ r, feats, star }) {
  const href = `/live-streaming-intel/${r.id}`;
  const blur = useDemoBlur().isBlurred(r.id);
  return (
    <tr className="border-t hairline hover:bg-white/[0.02] transition-colors" data-testid={`li-row-${r.id}`} data-blurred={blur ? "1" : "0"}>
      <td className="px-3 py-2.5 max-w-[240px]">
        <div className="flex items-baseline gap-1.5 min-w-0">
          {star && <StarButton on={star.has(r.id)} onToggle={() => star.toggle(r.id)} testid={`li-star-${r.id}`} size={13} />}
          <Link to={href} onClick={liveClick(r.id)} data-testid={`li-name-${r.id}`} className="text-white font-medium hover:text-cyan-400 truncate"><span className={bl(blur)}>{r.name}</span></Link>
          <GemMark id={r.id} testid={`li-gem-${r.id}`} />
          {r.n_streams != null && <span className="mono text-[10px] text-zinc-500 shrink-0" data-testid={`li-streams-${r.id}`}>({r.n_streams})</span>}
        </div>
        <div className="mono text-[10px] text-zinc-500 flex items-center gap-2 mt-0.5 flex-wrap">
          {r.yt && <a href={r.yt} target="_blank" rel="noreferrer" className={`hover:text-cyan-400 inline-flex items-center gap-1 ${bl(blur, "link")}`}>YT <ExternalLink size={9} /></a>}
          {r.tg && <a href={r.tg} target="_blank" rel="noreferrer" className={`hover:text-cyan-400 inline-flex items-center gap-1 ${bl(blur, "link")}`}>TG <Send size={9} /></a>}
          <span className="font-semibold" style={{ color: TIER_COLORS[r.tier] || "#94A3B8" }} data-testid={`li-tier-${r.id}`}>Tier {r.tier}</span>
          <span data-testid={`li-row-market-${r.id}`}>{MARKET_LABEL[r.market]}</span>
          {r.fav_outside && <span className="text-amber-300/70" data-testid={`li-fav-outside-${r.id}`}>Favourite · outside your filters</span>}
        </div>
      </td>
      <td className="px-3 py-2.5 mono text-zinc-200 whitespace-nowrap" data-testid={`li-subs-${r.id}`}>{short(r.subs)}</td>
      <td className="px-3 py-2.5 whitespace-nowrap" data-testid={`li-viewers-${r.id}`}>
        {r.measured ? (
          <>
            <div className="mono text-lg text-white leading-tight">{r.viewers != null ? r.viewers.toLocaleString() : "—"}</div>
            {r.peak != null && <div className="mono text-[10px] text-zinc-500">peak {r.peak.toLocaleString()}</div>}
          </>
        ) : <span className="text-zinc-500 text-xs" data-testid={`li-unmeasured-${r.id}`}>Not measured yet</span>}
      </td>
      <td className="px-3 py-2.5" data-testid={`li-often-${r.id}`}>
        <span className="mono text-[10px] px-1.5 py-0.5 rounded border hairline text-zinc-300">{OFTEN[r.group]}</span>
      </td>
      {feats.chat && <td className="px-3 py-2.5"><ChatCell r={r} speed={feats.speed} /></td>}
      <td className="px-3 py-2.5 text-sm" data-testid={`li-last-${r.id}`}><LastLive r={r} /></td>
      {feats.bot && <td className="px-3 py-2.5"><BotCell r={r} /></td>}
      <td className="px-3 py-2.5" data-testid={`li-exchanges-${r.id}`}><Exchanges r={r} /></td>
    </tr>
  );
}

export function LivePager({ total, page, size, setPage, setSize }) {
  const pages = Math.max(1, Math.ceil(total / size));
  const nums = [];
  for (let p = Math.max(1, page - 2); p <= Math.min(pages, page + 2); p++) nums.push(p);
  const b = "px-2.5 py-1 rounded border hairline text-xs transition-colors disabled:opacity-30 disabled:pointer-events-none hover:border-white/30";
  return (
    <div className="px-4 py-3 border-t hairline flex items-center justify-between gap-3 flex-wrap text-xs text-zinc-500" data-testid="li-pager">
      <label className="flex items-center gap-2">Rows per page
        <select value={size} onChange={(e) => setSize(Number(e.target.value))} data-testid="li-page-size"
          className="bg-transparent border hairline rounded px-1.5 py-1 text-zinc-200">
          {[10, 15].map((n) => <option key={n} value={n} className="bg-zinc-900">{n}</option>)}
        </select>
      </label>
      <div className="flex items-center gap-1.5 flex-wrap">
        <button type="button" className={b} disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="li-prev">Prev</button>
        {nums.map((p) => (
          <button key={p} type="button" onClick={() => setPage(p)} data-testid={`li-page-${p}`}
            className={`${b} ${p === page ? "text-white border-white/40 bg-white/10" : ""}`}>{p}</button>
        ))}
        <button type="button" className={b} disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="li-next">Next</button>
      </div>
    </div>
  );
}
